<script setup>
import { ref, onMounted, computed, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter, useRoute } from 'vue-router';
import mfaAPI from 'dashboard/api/mfa';
import { useAlert } from 'dashboard/composables';
import { emitter } from 'shared/helpers/mitt';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import MfaStatusCard from './MfaStatusCard.vue';
import MfaSetupWizard from './MfaSetupWizard.vue';
import MfaManagementActions from './MfaManagementActions.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';

const { t } = useI18n();
const router = useRouter();
const route = useRoute();

// State
const mfaEnabled = ref(false);
const backupCodesGenerated = ref(false);
const showSetup = ref(false);
const provisioningUri = ref('');
const qrCodeUrl = ref('');
const secretKey = ref('');
const backupCodes = ref([]);
const mfaMethod = ref('authenticator');
const mfaPhoneNumber = ref('');
const availableMethods = ref(['authenticator']);
const showDeliveryVerificationDialog = ref(false);
const deliveryVerificationCode = ref('');
const deliveryVerificationError = ref('');
const pendingDeliveryMethod = ref('');
const deliveryDialogRef = ref(null);
const otpDigits = ref(['', '', '', '', '', '']);
const otpInputRefs = ref([]);
const resendCountdown = ref(45);
let resendTimer = null;
const methodSectionTitle = 'Método de verificação';
const methodSectionDescription =
  'Escolha como deseja receber o segundo fator ao entrar na Talkvia.';
const phoneNumberLabel = 'Telefone para receber o código';
const phoneNumberPlaceholder = '5511999999999';
const savePreferenceLabel = 'Salvar preferência';
const verifyDeliveryLabel = 'Verificar código';
const deliveryCodeLabel = 'Código enviado';
const resendPromptLabel = 'Não recebeu o código?';
const mfaMethodOptions = [
  ['authenticator', 'Google Authenticator'],
  ['email', 'E-mail'],
  ['whatsapp', 'WhatsApp template'],
];

const availableMfaMethodOptions = computed(() => {
  const allowedMethods = availableMethods.value.length
    ? availableMethods.value
    : ['authenticator'];

  return mfaMethodOptions.filter(([value]) => allowedMethods.includes(value));
});

const parseApiErrorMessage = (error, fallbackMessage) => {
  const rawMessage = error?.response?.data?.error;
  if (!rawMessage) return fallbackMessage;

  if (String(rawMessage).includes('translation_missing')) {
    return fallbackMessage;
  }

  // Remove eventual HTML tags returned by server-side translation helper.
  return (
    String(rawMessage)
      .replace(/<[^>]+>/g, '')
      .trim() || fallbackMessage
  );
};

const maskedDestination = computed(() => {
  if (pendingDeliveryMethod.value === 'email') {
    return 'seu email da conta';
  }

  if (pendingDeliveryMethod.value === 'whatsapp') {
    const phone = mfaPhoneNumber.value || '';
    const suffix = phone.slice(-4);
    return suffix ? `*****${suffix}` : 'seu WhatsApp';
  }

  return 'seu dispositivo';
});

const canResend = computed(() => resendCountdown.value <= 0);
const joinedOtpCode = computed(() => otpDigits.value.join(''));
const resendButtonLabel = computed(() =>
  canResend.value ? 'Reenviar' : `Reenviar (${resendCountdown.value}s)`
);

const focusOtpInput = index => {
  const refItem = otpInputRefs.value[index];
  refItem?.focus?.();
};

const resetOtpInputs = () => {
  otpDigits.value = ['', '', '', '', '', ''];
  deliveryVerificationCode.value = '';
};

const stopResendCountdown = () => {
  if (resendTimer) {
    clearInterval(resendTimer);
    resendTimer = null;
  }
};

const startResendCountdown = () => {
  stopResendCountdown();
  resendCountdown.value = 45;
  resendTimer = setInterval(() => {
    if (resendCountdown.value <= 0) {
      stopResendCountdown();
      return;
    }

    resendCountdown.value -= 1;
  }, 1000);
};

// Component refs
const setupWizardRef = ref(null);
const managementActionsRef = ref(null);

// Load MFA status on mount
onMounted(async () => {
  try {
    const response = await mfaAPI.get();
    mfaEnabled.value = response.data.enabled;
    backupCodesGenerated.value = response.data.backup_codes_generated;
    availableMethods.value = response.data.available_methods || [
      'authenticator',
    ];
    mfaMethod.value = response.data.method || 'authenticator';
    if (!availableMethods.value.includes(mfaMethod.value)) {
      mfaMethod.value = availableMethods.value[0] || 'authenticator';
    }
    mfaPhoneNumber.value = response.data.phone_number || '';
  } catch (error) {
    if (error?.response?.status === 403) {
      router.push({
        name: 'profile_settings_index',
        params: {
          accountId: route.params.accountId,
        },
      });
    }
  }
});

// Start MFA setup
const startMfaSetup = async () => {
  try {
    const response = await mfaAPI.enable({
      method: mfaMethod.value,
      phone_number: mfaPhoneNumber.value,
    });

    if (response.data.pending_verification) {
      pendingDeliveryMethod.value =
        response.data.delivery_method || mfaMethod.value;
      showDeliveryVerificationDialog.value = true;
      resetOtpInputs();
      deliveryVerificationError.value = '';
      deliveryDialogRef.value?.open();
      setTimeout(() => focusOtpInput(0), 0);
      startResendCountdown();
      useAlert('Código enviado. Verifique para concluir a ativação.');
      return;
    }

    if (response.data.enabled && !response.data.provisioning_uri) {
      mfaEnabled.value = true;
      showSetup.value = false;
      useAlert(t('MFA_SETTINGS.SETUP.SUCCESS'));
      return;
    }

    // Store the provisioning URI
    provisioningUri.value =
      response.data.provisioning_uri || response.data.provisioning_url;

    // Store QR code URL if provided by backend
    if (response.data.qr_code_url) {
      qrCodeUrl.value = response.data.qr_code_url;
    }

    secretKey.value = response.data.secret;
    // Backup codes are now generated after verification, not during enable
    backupCodes.value = [];
    showSetup.value = true;
  } catch (error) {
    useAlert(
      parseApiErrorMessage(error, t('MFA_SETTINGS.SETUP.ERROR_STARTING'))
    );
  }
};

// Verify OTP code
const verifyCode = async verificationCode => {
  try {
    const response = await mfaAPI.verify(verificationCode, '');
    // Store backup codes returned from verification
    if (response.data.backup_codes) {
      backupCodes.value = response.data.backup_codes;
    }
    return true;
  } catch (error) {
    setupWizardRef.value?.handleVerificationError(
      error.response?.data?.error || t('MFA_SETTINGS.SETUP.INVALID_CODE')
    );
    throw error;
  }
};

// Complete MFA setup
const completeMfaSetup = () => {
  mfaEnabled.value = true;
  backupCodesGenerated.value = true;
  showSetup.value = false;
  emitter.emit(BUS_EVENTS.MFA_STATE_CHANGED);
  useAlert(t('MFA_SETTINGS.SETUP.SUCCESS'));
};

const verifyDeliveryCode = async () => {
  deliveryVerificationError.value = '';
  try {
    deliveryVerificationCode.value = joinedOtpCode.value;
    const response = await mfaAPI.verify(
      joinedOtpCode.value,
      pendingDeliveryMethod.value
    );
    if (response.data.backup_codes) {
      backupCodes.value = response.data.backup_codes;
    }
    mfaEnabled.value = true;
    backupCodesGenerated.value = true;
    showDeliveryVerificationDialog.value = false;
    deliveryDialogRef.value?.close();
    resetOtpInputs();
    stopResendCountdown();
    useAlert(t('MFA_SETTINGS.SETUP.SUCCESS'));
  } catch (error) {
    deliveryVerificationError.value = parseApiErrorMessage(
      error,
      t('MFA_SETTINGS.SETUP.INVALID_CODE')
    );
  }
};

const onDeliveryDialogClose = () => {
  showDeliveryVerificationDialog.value = false;
  resetOtpInputs();
  deliveryVerificationError.value = '';
  stopResendCountdown();
};

const onOtpInput = (index, event) => {
  const value = (event.target.value || '').replace(/\D/g, '').slice(-1);
  otpDigits.value[index] = value;

  if (value && index < otpDigits.value.length - 1) {
    focusOtpInput(index + 1);
  }
};

const onOtpKeydown = (index, event) => {
  if (event.key === 'Backspace' && !otpDigits.value[index] && index > 0) {
    focusOtpInput(index - 1);
  }

  if (event.key === 'ArrowLeft' && index > 0) {
    focusOtpInput(index - 1);
  }

  if (event.key === 'ArrowRight' && index < otpDigits.value.length - 1) {
    focusOtpInput(index + 1);
  }
};

const onOtpPaste = event => {
  const pasted = (event.clipboardData?.getData('text') || '')
    .replace(/\D/g, '')
    .slice(0, 6);
  if (!pasted) return;

  event.preventDefault();
  otpDigits.value = pasted.split('').concat(Array(6).fill('')).slice(0, 6);

  const nextIndex = Math.min(pasted.length, 5);
  focusOtpInput(nextIndex);
};

const resendDeliveryCode = async () => {
  if (!canResend.value) return;

  await startMfaSetup();
};

onBeforeUnmount(() => {
  stopResendCountdown();
});

// Cancel setup
const cancelSetup = () => {
  showSetup.value = false;
};

// Disable MFA
const disableMfa = async ({ password, otpCode, backupCode }) => {
  try {
    await mfaAPI.disable(password, { otpCode, backupCode });
    mfaEnabled.value = false;
    backupCodesGenerated.value = false;
    managementActionsRef.value?.resetDisableForm();
    emitter.emit(BUS_EVENTS.MFA_STATE_CHANGED);
    useAlert(t('MFA_SETTINGS.DISABLE.SUCCESS'));
  } catch (error) {
    useAlert(t('MFA_SETTINGS.DISABLE.ERROR'));
  }
};

const savePreferences = async () => {
  try {
    const response = await mfaAPI.updatePreferences({
      method: mfaMethod.value,
      phone_number: mfaPhoneNumber.value,
    });
    mfaMethod.value = response.data.method || mfaMethod.value;
    mfaPhoneNumber.value = response.data.phone_number || mfaPhoneNumber.value;
    useAlert('Preferências de 2FA salvas.');
  } catch (error) {
    useAlert('Não foi possível salvar as preferências de 2FA.');
  }
};

// Regenerate backup codes
const regenerateBackupCodes = async ({ otpCode }) => {
  try {
    const response = await mfaAPI.regenerateBackupCodes(otpCode);
    backupCodes.value = response.data.backup_codes;
    managementActionsRef.value?.resetRegenerateForm();
    managementActionsRef.value?.showBackupCodesDialog();
    emitter.emit(BUS_EVENTS.MFA_STATE_CHANGED);
    useAlert(t('MFA_SETTINGS.REGENERATE.SUCCESS'));
  } catch (error) {
    useAlert(t('MFA_SETTINGS.REGENERATE.ERROR'));
  }
};
</script>

<template>
  <div class="grid w-full">
    <BaseSettingsHeader
      :title="$t('MFA_SETTINGS.TITLE')"
      :description="$t('MFA_SETTINGS.SUBTITLE')"
      :back-button-label="$t('PROFILE_SETTINGS.TITLE')"
    />

    <div class="grid gap-4 w-full mt-4">
      <section
        class="rounded-xl border border-n-weak bg-n-solid-1 p-5 text-n-slate-12"
      >
        <h3 class="text-base font-semibold">{{ methodSectionTitle }}</h3>
        <p class="mt-1 text-sm text-n-slate-11">
          {{ methodSectionDescription }}
        </p>
        <div class="mt-4 grid gap-3 md:grid-cols-3">
          <label
            v-for="method in availableMfaMethodOptions"
            :key="method[0]"
            class="flex cursor-pointer items-center gap-2 rounded-lg border border-n-weak bg-n-solid-2 p-3 text-sm"
          >
            <input v-model="mfaMethod" type="radio" :value="method[0]" />
            <span>{{ method[1] }}</span>
          </label>
        </div>
        <label
          v-if="mfaMethod === 'whatsapp'"
          class="mt-4 flex max-w-md flex-col gap-1 text-sm"
        >
          {{ phoneNumberLabel }}
          <input
            v-model="mfaPhoneNumber"
            type="text"
            :placeholder="phoneNumberPlaceholder"
            class="h-10 rounded-lg border border-n-weak bg-n-solid-2 px-3 text-n-slate-12"
          />
        </label>
        <button
          type="button"
          class="mt-4 rounded-lg bg-n-brand px-4 py-2 text-sm font-medium text-white"
          @click="savePreferences"
        >
          {{ savePreferenceLabel }}
        </button>
      </section>

      <!-- MFA Status Card -->
      <MfaStatusCard
        :mfa-enabled="mfaEnabled"
        :show-setup="showSetup"
        @enable-mfa="startMfaSetup"
      />

      <!-- MFA Setup Wizard -->
      <MfaSetupWizard
        ref="setupWizardRef"
        :show-setup="showSetup"
        :mfa-enabled="mfaEnabled"
        :provisioning-uri="provisioningUri"
        :secret-key="secretKey"
        :backup-codes="backupCodes"
        :qr-code-url-prop="qrCodeUrl"
        @cancel="cancelSetup"
        @verify="verifyCode"
        @complete="completeMfaSetup"
      />

      <!-- MFA Management Actions -->
      <MfaManagementActions
        ref="managementActionsRef"
        :mfa-enabled="mfaEnabled"
        :backup-codes="backupCodes"
        @disable-mfa="disableMfa"
        @regenerate-backup-codes="regenerateBackupCodes"
      />

      <Dialog
        ref="deliveryDialogRef"
        type="edit"
        :title="verifyDeliveryLabel"
        :description="
          'Insira o código de verificação enviado para ' + maskedDestination
        "
        :confirm-button-label="verifyDeliveryLabel"
        :cancel-button-label="$t('MFA_SETTINGS.SETUP.CANCEL')"
        :disable-confirm-button="joinedOtpCode.length !== 6"
        @confirm="verifyDeliveryCode"
        @close="onDeliveryDialogClose"
      >
        <div class="space-y-4">
          <label class="block text-heading-3 text-n-slate-12">
            {{ deliveryCodeLabel }}
          </label>
          <div
            class="flex items-center justify-between gap-2"
            @paste="onOtpPaste"
          >
            <input
              v-for="(digit, index) in otpDigits"
              :key="index"
              :ref="el => (otpInputRefs[index] = el)"
              :value="digit"
              inputmode="numeric"
              maxlength="1"
              type="text"
              class="h-12 w-12 rounded-lg border border-n-slate-5 bg-n-solid-2 text-center text-lg font-semibold text-n-slate-12 outline-none transition focus:border-n-brand"
              @input="onOtpInput(index, $event)"
              @keydown="onOtpKeydown(index, $event)"
              @keyup.enter="verifyDeliveryCode"
            />
          </div>
          <p v-if="deliveryVerificationError" class="text-sm text-n-ruby-10">
            {{ deliveryVerificationError }}
          </p>
          <div
            class="flex items-center justify-center gap-1 text-sm text-n-slate-11"
          >
            <span>{{ resendPromptLabel }}</span>
            <button
              type="button"
              class="font-medium text-n-brand hover:underline disabled:text-n-slate-10 disabled:no-underline"
              :disabled="!canResend"
              @click="resendDeliveryCode"
            >
              {{ resendButtonLabel }}
            </button>
          </div>
        </div>
      </Dialog>
    </div>
  </div>
</template>
